
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/hooks/useLanguage";
import { AuthProvider } from "@/contexts/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";
import RedirectHandler from "@/components/RedirectHandler";
import LegalChatbot from "@/components/LegalChatbot";
import FloatingPhoneButton from "@/components/FloatingPhoneButton";
import AdminRoute from "@/components/admin/AdminRoute";
import AdminLayout from "@/components/admin/AdminLayout";
import SEO from "@/components/SEO";
import { multilingualRoutes, NotFound, BookingSuccess } from "@/lib/route-config";

// Admin pages
import AdminLogin from "@/pages/AdminLogin";
import AdminResetPassword from "@/pages/AdminResetPassword";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminAppointments from "@/pages/admin/Appointments";
import AdminCalendar from "@/pages/admin/Calendar";
import AdminBookings from "@/pages/admin/Bookings";
import AdminBlog from "@/pages/admin/Blog";
import AdminPages from "@/pages/admin/Pages";
import AdminPayments from "@/pages/admin/Payments";
import AdminIntegrations from "@/pages/admin/Integrations";
import AdminSettings from "@/pages/admin/Settings";
import SEOValidation from "@/pages/admin/SEOValidation";
import URLIndex from "@/pages/admin/URLIndex";
import SiteMap from "@/pages/admin/SiteMap";
import DynamicBlogPost from "@/pages/blog/DynamicBlogPost";

const queryClient = new QueryClient();

/**
 * Generates <Route> elements for all 3 languages from a multilingual route entry.
 */
const renderMultilingualRoutes = () =>
  multilingualRoutes.flatMap(({ routes, component: Component }, idx) =>
    (['en', 'es', 'vi'] as const).map((lang) => (
      <Route key={`${idx}-${lang}`} path={routes[lang]} element={<Component />} />
    ))
  );

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <BrowserRouter>
        <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <ScrollToTop />
            <RedirectHandler />
            <LegalChatbot />
            <FloatingPhoneButton />
            <SEO />
            <Routes>
              {/* All multilingual public routes */}
              {renderMultilingualRoutes()}

              {/* Booking success */}
              <Route path="/booking/success" element={<BookingSuccess />} />

              {/* Dynamic blog post routes */}
              <Route path="/blog/:slug" element={<DynamicBlogPost />} />
              <Route path="/es/blog/:slug" element={<DynamicBlogPost />} />
              <Route path="/vi/blog/:slug" element={<DynamicBlogPost />} />

              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/reset-password" element={<AdminResetPassword />} />
              <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                <Route index element={<AdminDashboard />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="appointments" element={<AdminRoute requiredSection="appointments"><AdminAppointments /></AdminRoute>} />
                <Route path="calendar" element={<AdminRoute requiredSection="calendar"><AdminCalendar /></AdminRoute>} />
                <Route path="bookings" element={<AdminRoute requiredSection="appointments"><AdminBookings /></AdminRoute>} />
                <Route path="blog" element={<AdminRoute requiredSection="blog"><AdminBlog /></AdminRoute>} />
                <Route path="pages" element={<AdminRoute requiredSection="pages"><AdminPages /></AdminRoute>} />
                <Route path="payments" element={<AdminRoute requiredSection="payments"><AdminPayments /></AdminRoute>} />
                <Route path="integrations" element={<AdminRoute requiredSection="integrations"><AdminIntegrations /></AdminRoute>} />
                <Route path="settings" element={<AdminRoute requiredSection="settings"><AdminSettings /></AdminRoute>} />
                <Route path="seo-validation" element={<SEOValidation />} />
                <Route path="url-index" element={<URLIndex />} />
                <Route path="sitemap" element={<SiteMap />} />
              </Route>

              {/* Catch-all */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </TooltipProvider>
        </LanguageProvider>
      </BrowserRouter>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
